import { useCallback, useRef, useState } from "react";
import {
	Modal,
	ModalOverlay,
	ModalContent,
	ModalHeader,
	ModalBody,
	ModalFooter,
	ModalCloseButton,
	Alert,
	AlertIcon,
	Text,
	Spinner,
} from "@chakra-ui/react";
import { FiUpload } from "react-icons/fi";

import { Button } from "newui/components/Button";
import { cn } from "utils/helpers";
import type {
	ScheduleColumnType,
	ScheduleImportPreview,
} from "./schedule-of-rates.types";
import { importScheduleFromExcel } from "../../api";

export interface ImportScheduleModalProps {
	hasExistingSchedule: boolean;
	inviteId: number;
	isOpen: boolean;
	onClose: () => void;
	onImport: (preview: ScheduleImportPreview) => void;
}

const COLUMN_TYPE_OPTIONS: { label: string; value: ScheduleColumnType }[] = [
	{ value: "text", label: "Text" },
	{ value: "unit_of_measurement", label: "Unit of Measurement" },
	{ value: "number", label: "Number" },
	{ value: "currency", label: "Currency" },
];

const cellBorder = "border border-[#EAECF0]";

const ImportScheduleModal: React.FC<ImportScheduleModalProps> = ({
	hasExistingSchedule,
	inviteId,
	isOpen,
	onClose,
	onImport,
}) => {
	const [file, setFile] = useState<File | null>(null);
	const [preview, setPreview] = useState<ScheduleImportPreview | null>(null);
	const [isUploading, setIsUploading] = useState(false);
	const [isConfirmed, setIsConfirmed] = useState(!hasExistingSchedule);
	const [error, setError] = useState("");
	const fileInputRef = useRef<HTMLInputElement>(null);

	const handleFileSelect = useCallback(
		async (e: React.ChangeEvent<HTMLInputElement>) => {
			const selectedFile = e.target.files?.[0];
			if (!selectedFile) {
				return;
			}

			const allowedTypes = [
				"application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
				"application/vnd.ms-excel",
			];
			if (!allowedTypes.includes(selectedFile.type)) {
				setError("Please select an Excel file (.xlsx or .xls)");
				return;
			}

			setFile(selectedFile);
			setError("");
			setIsUploading(true);

			try {
				const result = await importScheduleFromExcel(inviteId, selectedFile);
				setPreview(result);
			} catch {
				setError("Failed to parse the Excel file. Please check the format and try again.");
			} finally {
				setIsUploading(false);
			}
		},
		[inviteId]
	);

	const handleColumnTypeChange = useCallback(
		(colIndex: number, newType: ScheduleColumnType) => {
			if (!preview) {
				return;
			}
			const updatedColumns = [...preview.columns];
			updatedColumns[colIndex] = { ...updatedColumns[colIndex], detectedType: newType };
			setPreview({ ...preview, columns: updatedColumns });
		},
		[preview]
	);

	const handleColumnLabelChange = useCallback(
		(colIndex: number, label: string) => {
			if (!preview) {
				return;
			}
			const updatedColumns = [...preview.columns];
			updatedColumns[colIndex] = { ...updatedColumns[colIndex], label };
			setPreview({ ...preview, columns: updatedColumns });
		},
		[preview]
	);

	const handleCellEdit = useCallback(
		(rowIndex: number, colIndex: number, value: string) => {
			if (!preview) {
				return;
			}
			const updatedRows = preview.rows.map((row, rIdx) => {
				if (rIdx === rowIndex) {
					const newRow = [...row];
					newRow[colIndex] = value;
					return newRow;
				}
				return row;
			});
			setPreview({ ...preview, rows: updatedRows });
		},
		[preview]
	);

	const handleConfirmImport = useCallback(() => {
		if (!preview) {
			return;
		}
		onImport(preview);
		onClose();
	}, [preview, onImport, onClose]);

	const handleReset = useCallback(() => {
		setFile(null);
		setPreview(null);
		setError("");
		setIsConfirmed(!hasExistingSchedule);
		if (fileInputRef.current) {
			fileInputRef.current.value = "";
		}
	}, [hasExistingSchedule]);

	return (
		<Modal isOpen={isOpen} onClose={onClose} size="xl">
			<ModalOverlay />
			<ModalContent data-testid="import-schedule-modal">
				<ModalHeader>Import Schedule of Rates</ModalHeader>
				<ModalCloseButton />
				<ModalBody>
					{hasExistingSchedule && !isConfirmed && (
						<Alert
							status="warning"
							className="mb-4 rounded-md"
							data-testid="replace-warning"
						>
							<AlertIcon />
							<div>
								<Text className="text-sm font-medium">
									Importing will replace any existing schedule of rates
								</Text>
								<Text className="text-xs text-gray-600">
									This action cannot be undone. Your current schedule data will
									be overwritten.
								</Text>
								<Button
									size="xs"
									btntype="secondary"
									onClick={() => setIsConfirmed(true)}
									className="mt-2"
									data-testid="confirm-replace-btn"
								>
									I understand, continue
								</Button>
							</div>
						</Alert>
					)}

					{isConfirmed && !preview && !isUploading && (
						<div className="flex flex-col items-center justify-center rounded-lg border-2 border-dashed border-gray-300 p-8">
							<FiUpload className="mb-3 text-gray-400" size={32} />
							<Text className="mb-2 text-sm font-medium text-gray-700">
								Select an Excel file to import
							</Text>
							<Text className="mb-4 text-xs text-gray-500">
								Supports .xlsx and .xls files
							</Text>
							<input
								ref={fileInputRef}
								type="file"
								accept=".xlsx,.xls"
								onChange={handleFileSelect}
								className="hidden"
								data-testid="file-input"
							/>
							<Button
								size="sm"
								btntype="primary"
								onClick={() => fileInputRef.current?.click()}
								data-testid="select-file-btn"
							>
								Choose File
							</Button>
						</div>
					)}

					{isUploading && (
						<div
							className="flex flex-col items-center justify-center py-8"
							data-testid="upload-spinner"
						>
							<Spinner size="lg" className="mb-3" />
							<Text className="text-sm text-gray-600">
								Analyzing spreadsheet...
							</Text>
						</div>
					)}

					{error && (
						<Alert
							status="error"
							className="mt-3 rounded-md"
							data-testid="import-error"
						>
							<AlertIcon />
							<Text className="text-sm">{error}</Text>
						</Alert>
					)}

					{preview && (
						<div data-testid="import-preview">
							<div className="mb-3 flex items-center justify-between">
								<Text className="text-sm font-medium text-gray-700">
									Preview — {preview.columns.length} columns,{" "}
									{preview.rows.length} rows detected
								</Text>
								<Button
									size="xs"
									btntype="secondary"
									onClick={handleReset}
									data-testid="reset-import-btn"
								>
									Choose Different File
								</Button>
							</div>
							<div className="overflow-x-auto rounded-lg border border-[#EAECF0]">
								<table className="w-full table-auto">
									<caption className="sr-only">Import preview</caption>
									<thead>
										<tr className="bg-blue-50">
											<th
												className={cn(cellBorder, "w-10 px-2 py-1 text-center text-xs font-medium text-gray-500")}
											>
												#
											</th>
											{preview.columns.map((col, idx) => (
												<th
													key={idx}
													className={cn(
														cellBorder,
														"px-2 py-1"
													)}
													style={{ minWidth: "140px" }}
												>
													<select
														className="w-full rounded border border-gray-300 bg-white px-1 py-0.5 text-xs"
														value={col.detectedType}
														onChange={(e) =>
															handleColumnTypeChange(
																idx,
																e.target.value as ScheduleColumnType
															)
														}
														aria-label={`Column type for column ${idx + 1}`}
													data-testid={`col-type-select-${idx}`}
													>
														{COLUMN_TYPE_OPTIONS.map((opt) => (
															<option
																key={opt.value}
																value={opt.value}
															>
																{opt.label}
															</option>
														))}
													</select>
												</th>
											))}
										</tr>
										<tr className="bg-[#F9FAFB]">
											<th className={cn(cellBorder, "w-10 px-2 py-1")} />
											{preview.columns.map((col, idx) => (
												<th
													key={idx}
													className={cn(
														cellBorder,
														"px-2 py-1"
													)}
												>
													<input
														type="text"
														className="w-full rounded border border-gray-300 bg-white px-1 py-0.5 text-xs font-medium"
														value={col.label}
														onChange={(e) =>
															handleColumnLabelChange(
																idx,
																e.target.value
															)
														}
														aria-label={`Column label for column ${idx + 1}`}
													data-testid={`col-label-input-${idx}`}
													/>
												</th>
											))}
										</tr>
									</thead>
									<tbody>
										{preview.rows.map((row, rIdx) => (
											<tr key={rIdx}>
												<td className={cn(cellBorder, "w-10 text-center text-xs text-gray-500 bg-gray-50")}>
													{rIdx + 1}
												</td>
												{row.map((cell, cIdx) => (
													<td
														key={cIdx}
														className={cn(cellBorder, "px-1 py-1")}
													>
														<input
															type="text"
															className="w-full rounded border border-transparent bg-transparent px-1 py-0.5 text-xs hover:border-gray-300 focus:border-blue-400 focus:outline-none"
															value={cell}
															onChange={(e) =>
																handleCellEdit(
																	rIdx,
																	cIdx,
																	e.target.value
																)
															}
															data-testid={`cell-${rIdx}-${cIdx}`}
														/>
													</td>
												))}
											</tr>
										))}
									</tbody>
								</table>
							</div>
						</div>
					)}
				</ModalBody>
				<ModalFooter>
					<Button
						btntype="secondary"
						onClick={onClose}
						className="mr-3"
						data-testid="cancel-import"
					>
						Cancel
					</Button>
					{preview && (
						<Button
							btntype="primary"
							onClick={handleConfirmImport}
							data-testid="confirm-import-btn"
						>
							Import
						</Button>
					)}
				</ModalFooter>
			</ModalContent>
		</Modal>
	);
};

export { ImportScheduleModal };
